import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { api } from "@/lib/api";
import {
  type DateFilter,
  type FilterValue,
  type NumberFilter,
  processQueryParams,
  type QueryParams,
} from "@/lib/process-query-params";

export const ACCOUNTS_QUERY_KEY = ["datahub", "accounts"] as const;

export interface Account {
  id: string;
  name: string;
  type: string | null;
  record_type_name: string | null;
  created_date: string | null;
  source_record_id: string;
  source_record_type: string;
  parent_id: string | null;
  customer_org_id: string;
  contact_count: number;
  opportunity_count: number;
  won_opportunity_count: number;
  open_opportunity_count: number;
  website: string | null;
  // Touchpoint metrics (calculated via MetricRenderer)
  touchpoint_count?: number;
  first_touchpoint_date?: string | null;
  last_touchpoint_date?: string | null;
}

export interface AccountsResponse {
  results: Account[];
  total: number;
  limit: number;
  offset: number;
  client_warning?: string; // Warning from server (e.g., "SORT_ON_METRIC_TOO_MANY_MATCHING")
}

// Re-export the filter types for convenience
export type { DateFilter, FilterValue, NumberFilter };

/**
 * Hook for fetching accounts by IDs
 */
export function useAccountsByIds(accountIds: string[] | undefined) {
  const searchParams = {
    account_ids: accountIds?.join(",") ?? "",
  };

  const endpoint = `datahub/accounts/by-ids/${
    searchParams ? `?${new URLSearchParams(searchParams)}` : ""
  }`;

  return api.useQuery<AccountsResponse>(endpoint, {
    enabled: !!accountIds?.length,
    // Cache the result for longer to avoid unnecessary refetches
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

interface UseAccountSearchOptions {
  search?: string;
  enabled?: boolean;
}

/**
 * Hook for searching accounts
 */
export function useAccountSearch(options: UseAccountSearchOptions = {}) {
  const debouncedSearch = useDebounce(options.search, 300);

  return useQuery<AccountsResponse, Error>({
    queryKey: ["datahub/accounts/search", { search: debouncedSearch }],
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (debouncedSearch) {
        searchParams.set("search", debouncedSearch);
      }

      return api.get<AccountsResponse>(
        `datahub/accounts/search/?${searchParams}`,
      );
    },
    enabled: options.enabled !== false && !!debouncedSearch,
  });
}

/**
 * Hook for fetching accounts with infinite scrolling
 */
export function useInfiniteAccounts(params?: QueryParams) {
  return useInfiniteQuery<AccountsResponse, Error>({
    queryKey: ["datahub/accounts/infinite", params],
    initialPageParam: 0,
    enabled: !!params,
    queryFn: async ({ pageParam }) =>
      api.get<AccountsResponse>("datahub/accounts/", {
        params: Object.fromEntries(
          Object.entries({
            ...params,
            offset: String(pageParam),
          }).map(([k, v]) => [k, v != null ? String(v) : ""]),
        ),
      }),
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (acc, page) => acc + page.results.length,
        0,
      );
      return totalFetched < lastPage.total ? totalFetched : undefined;
    },
  });
}

/**
 * Hook for fetching all accounts matching the given filters for CSV export
 * This leverages the same API endpoint and parameter structure as useInfiniteAccounts
 * Returns a function that can be called to trigger the export, rather than fetching immediately
 */
export function useAccountsExport() {
  const mutation = useMutation<
    Account[],
    Error,
    QueryParams | undefined,
    unknown
  >({
    mutationFn: async (params = {}) => {
      const allAccounts: Account[] = [];
      let offset = 0;
      let hasMoreData = true;

      // Paginate through all accounts
      while (hasMoreData) {
        const queryParams: QueryParams = {
          ...params,
          limit: 50000, // Override the limit for export
          offset,
        };

        const searchParams = processQueryParams(queryParams);

        // Fetch the current page of accounts
        const response = await api.get<AccountsResponse>(
          `datahub/accounts/?${searchParams}`,
        );

        // Add the results to our array
        allAccounts.push(...response.results);

        // Check if we should continue fetching
        hasMoreData = allAccounts.length < response.total;
        // Increment the offset for the next page
        offset += response.results.length;
      }

      return allAccounts;
    },
  });

  // Return the mutation object and a convenience trigger function
  return {
    ...mutation,
    exportAccounts: (params?: QueryParams) => mutation.mutate(params),
    exportAccountsAsync: (params?: QueryParams) => mutation.mutateAsync(params),
  };
}
